import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  TextField,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { API_URL } from "../../config";

// --- Edit Dialog ---
const EditDialog = ({ open, onClose, onSave, data, title, showHeading }) => {
  const [form, setForm] = useState({ value: "", url: "", heading: "" });
  useEffect(() => {
    if (data) setForm({ value: data.value || "", url: data.url || "", heading: data.heading || "" });
    else setForm({ value: "", url: "", heading: "" });
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField fullWidth label="Name" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} margin="dense" />
        <TextField fullWidth label="URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} margin="dense" helperText="Use # if no link" />
        {showHeading && (
          <TextField fullWidth label="Under Heading" value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} margin="dense" helperText="e.g. Necklace, Price Range, Earrings" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(form)} color="primary" variant="contained" disabled={!form.value.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export const NavMenu = () => {
  const [tab, setTab] = useState(0);
  const [desktopMenu, setDesktopMenu] = useState([]);
  const [mobileMenu, setMobileMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState({ open: false, title: "", data: null, callback: null, showHeading: false });

  const [syncMenus, setSyncMenus] = useState(true);

  const currentMenu = tab === 0 ? desktopMenu : mobileMenu;
  const setCurrentMenu = (newMenu) => {
    if (tab === 0) {
      setDesktopMenu(newMenu);
      if (syncMenus) setMobileMenu(newMenu);
    } else {
      setMobileMenu(newMenu);
      if (syncMenus) setDesktopMenu(newMenu);
    }
  };

  useEffect(() => { fetchMenu(); }, []);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL + "/navmenu");
      const data = await res.json();
      setDesktopMenu(data.desktop || []);
      setMobileMenu(data.mobile || []);
    } catch (err) {
      setSnack({ open: true, msg: "Failed to fetch: " + err.message, severity: "error" });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(API_URL + "/navmenu", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ desktop: desktopMenu, mobile: mobileMenu }),
      });
      const result = await res.json();
      setSnack({ open: true, msg: res.ok ? "Saved successfully!" : result.message, severity: res.ok ? "success" : "error" });
    } catch (err) {
      setSnack({ open: true, msg: "Error: " + err.message, severity: "error" });
    }
    setSaving(false);
  };

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));
  const openDialog = (title, data, callback, showHeading = false) => setDialog({ open: true, title, data, callback, showHeading });
  const closeDialog = () => setDialog({ open: false, title: "", data: null, callback: null, showHeading: false });

  // --- TOP LEVEL MENU ---
  const addMenu = () => openDialog("Add Menu Item", null, (form) => {
    setCurrentMenu([...currentMenu, { title: form.value, key: String(Date.now()), url: form.url || "#", Submenu: [[]], expand: true }]);
    closeDialog();
  });

  const editMenu = (item) => openDialog("Edit Menu Item", { value: item.title, url: item.url }, (form) => {
    setCurrentMenu(currentMenu.map((m) => m.key === item.key ? { ...m, title: form.value, url: form.url } : m));
    closeDialog();
  });

  const deleteMenu = (item) => { if (window.confirm(`Delete "${item.title}"?`)) setCurrentMenu(currentMenu.filter((m) => m.key !== item.key)); };

  const moveMenu = (index, dir) => {
    const arr = [...currentMenu];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setCurrentMenu(arr);
  };

  // --- SUBMENU (Necklace, Earring, Bangle etc) ---
  const addSubmenu = (menuItem) => openDialog("Add Submenu Item", null, (form) => {
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      const sub = m.Submenu ? [...m.Submenu] : [[]];
      sub[0] = [...(sub[0] || []), { value: form.value, title: null, isLink: true, url: form.url || "#" }];
      return { ...m, Submenu: sub };
    }));
    closeDialog();
  });

  const editSubmenu = (menuItem, subItem) => openDialog("Edit Submenu Item", { value: subItem.value, url: subItem.url }, (form) => {
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      const sub = m.Submenu.map((arr) => arr.map((s) => s === subItem ? { ...s, value: form.value, url: form.url } : s));
      return { ...m, Submenu: sub };
    }));
    closeDialog();
  });

  const deleteSubmenu = (menuItem, subItem) => {
    if (!window.confirm(`Delete "${subItem.value}"?`)) return;
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      return { ...m, Submenu: m.Submenu.map((arr) => arr.filter((s) => s !== subItem)) };
    }));
  };

  // --- CHILD ITEMS (Short Necklace, Under 5000 etc) ---
  const addChild = (menuItem, subItem) => openDialog("Add Child Item", { heading: subItem.children?.[0]?.heading || "", url: "" }, (form) => {
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      const sub = m.Submenu.map((arr) => arr.map((s) => {
        if (s !== subItem) return s;
        const children = s.children ? JSON.parse(JSON.stringify(s.children)) : [];
        const heading = form.heading || "General";
        const section = children.find((c) => c.heading === heading);
        if (section) section.items.push({ value: form.value, url: form.url || "#" });
        else children.push({ heading, url: "", items: [{ value: form.value, url: form.url || "#" }] });
        return { ...s, children };
      }));
      return { ...m, Submenu: sub };
    }));
    closeDialog();
  }, true);

  const editChild = (menuItem, subItem, sectionIdx, childIdx) => {
    const child = subItem.children[sectionIdx].items[childIdx];
    const heading = subItem.children[sectionIdx].heading;
    openDialog("Edit Child Item", { value: child.value, url: child.url, heading }, (form) => {
      setCurrentMenu(currentMenu.map((m) => {
        if (m.key !== menuItem.key) return m;
        const sub = m.Submenu.map((arr) => arr.map((s) => {
          if (s !== subItem) return s;
          const children = JSON.parse(JSON.stringify(s.children));
          // Remove from old section
          children[sectionIdx].items.splice(childIdx, 1);
          // Add to target section
          const targetHeading = form.heading || heading;
          let targetSection = children.find((c) => c.heading === targetHeading);
          if (!targetSection) { targetSection = { heading: targetHeading, items: [] }; children.push(targetSection); }
          targetSection.items.push({ value: form.value, url: form.url });
          // Clean empty sections
          const cleaned = children.filter((c) => c.items.length > 0);
          return { ...s, children: cleaned.length > 0 ? cleaned : undefined };
        }));
        return { ...m, Submenu: sub };
      }));
      closeDialog();
    }, true);
  };

  const deleteChild = (menuItem, subItem, sectionIdx, childIdx) => {
    const child = subItem.children[sectionIdx].items[childIdx];
    if (!window.confirm(`Delete "${child.value}"?`)) return;
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      const sub = m.Submenu.map((arr) => arr.map((s) => {
        if (s !== subItem) return s;
        const children = JSON.parse(JSON.stringify(s.children));
        children[sectionIdx].items.splice(childIdx, 1);
        const cleaned = children.filter((c) => c.items.length > 0);
        return { ...s, children: cleaned.length > 0 ? cleaned : undefined };
      }));
      return { ...m, Submenu: sub };
    }));
  };

  // --- HEADING (Necklace, Price Range section titles) ---
  const editHeading = (menuItem, subItem, sectionIdx) => {
    const section = subItem.children[sectionIdx];
    openDialog("Edit Heading", { value: section.heading, url: section.url || "" }, (form) => {
      setCurrentMenu(currentMenu.map((m) => {
        if (m.key !== menuItem.key) return m;
        const sub = m.Submenu.map((arr) => arr.map((s) => {
          if (s !== subItem) return s;
          const children = JSON.parse(JSON.stringify(s.children));
          children[sectionIdx].heading = form.value;
          children[sectionIdx].url = form.url || "";
          return { ...s, children };
        }));
        return { ...m, Submenu: sub };
      }));
      closeDialog();
    });
  };

  const deleteHeading = (menuItem, subItem, sectionIdx) => {
    const section = subItem.children[sectionIdx];
    if (!window.confirm(`Delete heading "${section.heading}" and all its items?`)) return;
    setCurrentMenu(currentMenu.map((m) => {
      if (m.key !== menuItem.key) return m;
      const sub = m.Submenu.map((arr) => arr.map((s) => {
        if (s !== subItem) return s;
        const children = JSON.parse(JSON.stringify(s.children));
        children.splice(sectionIdx, 1);
        return { ...s, children: children.length > 0 ? children : undefined };
      }));
      return { ...m, Submenu: sub };
    }));
  };

  if (loading) return <Grid container justifyContent="center" style={{ paddingTop: 100 }}><CircularProgress /></Grid>;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography component="h6" variant="h6">Nav Menu</Typography>
        <Typography variant="body2" color="textSecondary">Manage the navigation menu shown on stylori.com</Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper><Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Desktop Menu" /><Tab label="Mobile Menu" />
        </Tabs></Paper>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 10 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving && <CircularProgress size={16} style={{ marginRight: 6 }} />} Save Menu
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addMenu}>Add Menu Item</Button>
          <Button variant="outlined" color="secondary" onClick={fetchMenu}>Reset</Button>
          <FormControlLabel
            control={<Switch checked={syncMenus} onChange={(e) => setSyncMenus(e.target.checked)} color="primary" />}
            label="Sync Desktop & Mobile"
            style={{ marginLeft: 16 }}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                <TableCell style={{ fontWeight: 700, width: "30%" }}>Menu Item</TableCell>
                <TableCell style={{ fontWeight: 700 }}>URL</TableCell>
                <TableCell align="right" style={{ fontWeight: 700, width: 180 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentMenu.map((menuItem, menuIdx) => {
                const menuKey = `menu-${menuItem.key}`;
                const menuOpen = expanded[menuKey];
                const flatSubs = menuItem.Submenu ? menuItem.Submenu.flat() : [];
                return (
                  <React.Fragment key={menuItem.key}>
                    {/* TOP LEVEL */}
                    <TableRow hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {flatSubs.length > 0 && (
                            <IconButton size="small" onClick={() => toggle(menuKey)}>
                              {menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                          <Typography style={{ fontWeight: 700, fontSize: 15 }}>{menuItem.title}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell style={{ fontSize: 12, color: "#555" }}>{menuItem.url}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => moveMenu(menuIdx, -1)} disabled={menuIdx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => moveMenu(menuIdx, 1)} disabled={menuIdx === currentMenu.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => editMenu(menuItem)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => deleteMenu(menuItem)}><DeleteIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => addSubmenu(menuItem)} title="Add submenu"><AddIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>

                    {/* SUBMENUS */}
                    {menuOpen && flatSubs.map((subItem, subIdx) => {
                      const subKey = `sub-${menuItem.key}-${subIdx}`;
                      const subOpen = expanded[subKey];
                      const hasChildren = subItem.children && subItem.children.length > 0;
                      return (
                        <React.Fragment key={subKey}>
                          <TableRow hover style={{ backgroundColor: "#fafafa" }}>
                            <TableCell style={{ paddingLeft: 48 }}>
                              <Box display="flex" alignItems="center">
                                {hasChildren && (
                                  <IconButton size="small" onClick={() => toggle(subKey)}>
                                    {subOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  </IconButton>
                                )}
                                <Typography style={{ fontWeight: 600, fontSize: 14 }}>{subItem.value}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell style={{ fontSize: 12, color: "#555" }}>{subItem.url}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => editSubmenu(menuItem, subItem)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteSubmenu(menuItem, subItem)}><DeleteIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => addChild(menuItem, subItem)} title="Add child"><AddIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>

                          {/* CHILDREN grouped by heading */}
                          {subOpen && hasChildren && subItem.children.map((section, sectionIdx) => (
                            <React.Fragment key={`${subKey}-sec-${sectionIdx}`}>
                              {/* Heading row with URL and edit */}
                              <TableRow style={{ backgroundColor: "#f4fffe" }}>
                                <TableCell style={{ paddingLeft: 72, paddingTop: 8, paddingBottom: 8 }}>
                                  <Typography variant="caption" style={{ fontWeight: 700, color: "#06ab9f", textTransform: "uppercase", letterSpacing: 1 }}>
                                    {section.heading}
                                  </Typography>
                                </TableCell>
                                <TableCell style={{ fontSize: 12, color: "#555" }}>{section.url || ""}</TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => editHeading(menuItem, subItem, sectionIdx)} title="Edit heading"><EditIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={() => deleteHeading(menuItem, subItem, sectionIdx)} title="Delete heading & its items"><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                              {/* Items under heading */}
                              {section.items.map((child, childIdx) => (
                                <TableRow key={`${subKey}-${sectionIdx}-${childIdx}`} style={{ backgroundColor: "#f9f9f9" }}>
                                  <TableCell style={{ paddingLeft: 92 }}>↳ {child.value}</TableCell>
                                  <TableCell style={{ fontSize: 12, color: "#555" }}>{child.url}</TableCell>
                                  <TableCell align="right">
                                    <IconButton size="small" onClick={() => editChild(menuItem, subItem, sectionIdx, childIdx)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => deleteChild(menuItem, subItem, sectionIdx, childIdx)}><DeleteIcon fontSize="small" /></IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {currentMenu.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center">No menu items. Click "Add Menu Item" to start.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <EditDialog open={dialog.open} onClose={closeDialog} onSave={dialog.callback || (() => {})} data={dialog.data} title={dialog.title} showHeading={dialog.showHeading} />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Grid>
  );
};

export default NavMenu;
